import { FilterTemplate, NULL_AS_STRING } from '@repo/shared/dtos/beneficiary'
import { useT } from '@repo/ui/hooks'
import { Label } from '@repo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { PlusIcon, XIcon } from 'lucide-react'
import { useState } from 'react'

import { useAbility } from '@/ability/ability-context'
import { FilterTemplateCreator } from '@/components/beneficiary/list/filter-template/FilterTemplateCreator'
import { defaultFilterTemplate } from '@/store/beneficiary-list-filters/slice'
import { CurrentFilterTemplate } from '@/store/beneficiary-list-filters/types'

export function FilterTemplateSelectButton({
  disabled,
  currentFilterTemplate,
  onSelect,
  savedFilterTemplates,
}: {
  disabled: boolean
  currentFilterTemplate: CurrentFilterTemplate
  onSelect: (filterTemplate: CurrentFilterTemplate) => void
  savedFilterTemplates: FilterTemplate[]
}) {
  const { tc } = useT()
  const ability = useAbility()

  const [open, setOpen] = useState(false)

  const isCurrentFilterTemplateLoaded = (filterTemplate: FilterTemplate) =>
    'id' in currentFilterTemplate && currentFilterTemplate.id === filterTemplate.id

  const handleOnLoad = (filterTemplate: FilterTemplate) => {
    if (isCurrentFilterTemplateLoaded(filterTemplate)) {
      return
    }
    onSelect(filterTemplate)
  }
  const handleSelect = (name: string) => {
    const filterTemplate = savedFilterTemplates.find((ft) => ft.name === name)
    if (filterTemplate) {
      handleOnLoad(filterTemplate)
    } else if (name === '__create__') {
      // Create
      setOpen(true)
    } else {
      // NULL selected
      onSelect(defaultFilterTemplate)
    }
  }
  const isDisabled = disabled || !savedFilterTemplates
  return (
    <div className="flex flex-col gap-1">
      <Label className="justify-between flex flex-1">
        {tc('filterTemplate', { cfl: true })}

        {'id' in currentFilterTemplate && (
          <span
            className="text-xs text-primary-400 hover:underline hover:text-primary-500 transition-colors cursor-pointer"
            onClick={() => handleSelect(NULL_AS_STRING)}
          >
            {tc('unselect', { cfl: true })}
          </span>
        )}
      </Label>

      <Select onValueChange={handleSelect} value={currentFilterTemplate.name}>
        <SelectTrigger className="min-w-60 w-fit" disabled={isDisabled}>
          <SelectValue placeholder={tc('noSelection', { cfl: true })} />
        </SelectTrigger>
        <SelectContent position="popper">
          {savedFilterTemplates?.map((filter) => (
            <SelectItem key={filter.id} value={filter.name}>
              {filter.name}
            </SelectItem>
          ))}

          {currentFilterTemplate.name && (
            <SelectItem value={NULL_AS_STRING}>
              <div className="flex gap-2 items-center">
                <XIcon className="size-4 text-primary-300" />
                {tc('noFilterTemplate', { cfl: true })}
              </div>
            </SelectItem>
          )}

          {ability.can('accessRoute', 'admin') && (
            <SelectItem value={'__create__'}>
              <div className="flex gap-2 items-center">
                <PlusIcon className="size-4 text-primary-300" />{' '}
                {tc('createFilterTemplate', { cfl: true })}
              </div>
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      {ability.can('accessRoute', 'admin') && (
        <FilterTemplateCreator modalHandler={{ open, setOpen }} />
      )}
    </div>
  )
}
